import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ResponsiveLogo } from '../components/ThemeLogo';
import { Moon, Sun, ArrowLeft } from 'lucide-react';

export default function FairUsePage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="p-2 hover:bg-muted rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></Link>
              <Link to="/"><ResponsiveLogo className="h-8 w-auto" /></Link>
            </div>
            <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-muted transition-colors">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="fair-use-page">
        <h1 className="font-heading text-3xl font-bold mb-2">Fair Use Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: August 2026</p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6">
          <p className="text-muted-foreground">
            Odapto is designed to help users explore ideas, analyze businesses, solve problems, and support better decision-making. To keep the platform useful and available for everyone, we ask users to use Odapto reasonably and responsibly.
          </p>

          <p className="text-muted-foreground">
            You may use Odapto and its features for your own business, professional, educational, or personal purposes, subject to your subscription and these Terms.
          </p>

          <div>
            <h2 className="text-xl font-semibold mb-3">You may not:</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Use Odapto for unlawful, fraudulent, harmful, or abusive activities.</li>
              <li>Attempt to copy, reverse engineer, reproduce, resell, or commercially exploit Odapto, its platform, methods, templates, prompts, or underlying technology without our permission.</li>
              <li>Use automated systems, bots, scraping tools, or similar methods to access the platform excessively or extract large amounts of content or data.</li>
              <li>Share access credentials or allow others to use your account beyond what your subscription permits.</li>
              <li>Use the service in a way that creates excessive system load, materially increases third-party AI or infrastructure costs, or negatively affects other users.</li>
              <li>Circumvent usage limits, security measures, access controls, or other technical restrictions.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3">Enforcement</h2>
            <p className="text-muted-foreground mb-3">
              Odapto may apply reasonable usage limits where necessary to protect the platform, maintain service quality, control excessive usage or costs, or prevent misuse.
            </p>
            <p className="text-muted-foreground">
              If we believe use is unreasonable or violates this policy, we may contact the user, temporarily limit certain features, or suspend access in serious or repeated cases.
            </p>
          </div>

          <p className="text-muted-foreground">
            We aim to apply this policy reasonably and fairly. Normal use of Odapto for genuine business analysis, strategy, problem-solving, and decision support should not be affected.
          </p>

          <p className="text-muted-foreground pt-4">
            This Fair Use Policy is part of and subject to our <Link to="/terms" className="text-odapto-orange hover:underline">Terms &amp; Conditions</Link>.
          </p>

          <div className="pt-6 border-t border-border flex flex-wrap gap-4 text-sm">
            <Link to="/terms" className="text-odapto-orange hover:underline">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="text-odapto-orange hover:underline">Privacy Policy</Link>
            <Link to="/contact" className="text-odapto-orange hover:underline">Contact Us</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
